import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import clsx from "clsx";

// Components
import TileSelectBox from "./TileSelectBox";

// MUI
import { ClickAwayListener, Grow, Paper, Stack, Tooltip, Typography } from "@mui/material";

// Icons
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import CircularProgress from "@mui/material/CircularProgress";

const getMaxHeight = (items = 0, args = {}) => {
	const { liPadding = 6, liHeight = 22, liMargin = 2 } = args;
	return items * liPadding + liHeight * items + liMargin - (items - 2);
};

const MIN_ITEMS_TO_SHOW = 5;

const isDisabledSelector = (data, noExpandible, disabled = false) => {
	if (!data?.length || noExpandible) {
		return true;
	}
	return disabled;
};

const getOpenProperties = (open = false) => (open ? { timeout: 200 } : {});

const getTooltipTitle = (title = "", open = false) => {
	if (open) {
		return "";
	}
	return title;
};
export default function TileSelectButton(props) {
	const {
		data = [],
		label = "",
		tooltip = "",
		loading = false,
		selectBy = "_id",
		showBy = "label",
		sortBy = "value",
		className,
		icon,
		iconHover,
		maxItems,
		noExpandible,
		disabled,
		selectDefaultValue,
		sx = null,
		onSelect = () => {},
	} = props;

	const [open, setOpen] = useState(false);
	const [defaultValue, setDefaultValue] = useState(null);
	const [maxHeightArea, setMaxHeightArea] = useState(getMaxHeight(MIN_ITEMS_TO_SHOW));

	const isDisabled = isDisabledSelector(data, noExpandible, disabled);

	const basicIcon = icon || <AddCircleOutlineRoundedIcon className="tile-icon default-icon" fontSize="medium" />;

	const [basicIconHover, setBasicIconHover] = useState(
		iconHover || <AddCircleRounded className="tile-icon hover-icon" fontSize="medium" />
	);

	const handleOpen = (e) => {
		if (noExpandible) {
			!isDisabled && onSelect?.(e);
			return;
		}

		if (!isDisabled) {
			setOpen(e.target.className !== "MuiAutocomplete-option Mui-focused");
		}
	};

	const handleOnChange = (value) => {
		if (value) {
			setDefaultValue(value);
			onSelect?.(value);
		}
	};

	useEffect(() => {
		if (selectDefaultValue) {
			setDefaultValue(selectDefaultValue);
		}

		if (maxItems) {
			let reason = maxItems;
			if (data?.length && reason > data?.length) {
				reason = MIN_ITEMS_TO_SHOW;
			}
			setMaxHeightArea(getMaxHeight(reason));
		}

		const isPresentBasicIcon = icon && icon !== <AddCircleOutlineRoundedIcon className="tile-icon default-icon" fontSize="medium" />;
		if (isPresentBasicIcon && (!iconHover || typeof iconHover === "undefined")) {
			setBasicIconHover("");
		}

		return () => {
			setOpen(false);
			setDefaultValue(null);
			setMaxHeightArea(getMaxHeight(MIN_ITEMS_TO_SHOW));
			setBasicIconHover(<AddCircleRounded className="tile-icon hover-icon" fontSize="medium" />);
		};
	}, [data, selectDefaultValue, noExpandible, maxItems, icon, iconHover]);

	return (
		<ClickAwayListener onClickAway={() => setOpen(false)}>
			<Tooltip arrow placement={"right"} title={getTooltipTitle(tooltip, open)}>
				<Paper
					variant="outlined"
					square
					className={clsx({
						[className]: Boolean(className),
						"tile-select-btn": true,
						"tile-select-open": open,
						"tile-select-disabled": isDisabled || loading,
					})}
					onClick={handleOpen}
					sx={{ "&": { ...sx }, "& .MuiAutocomplete-popper .MuiPaper-root": { maxHeight: `${maxHeightArea}px` } }}
				>
					{loading ? (
						<CircularProgress className="tile-select-loading" color="inherit" size={20} sx={{ color: "var(--neutral-gray)" }} />
					) : (
						<Stack position={"relative"} alignContent={"center"} justifyContent={"center"}>
							<Stack>{basicIcon}</Stack>
							<Stack position={"absolute"}>{basicIconHover}</Stack>
						</Stack>
					)}

					{open ? (
						<Grow in={open} {...getOpenProperties(open)}>
							<Stack w={1}>
								<TileSelectBox
									data={data}
									defaultValue={defaultValue}
									selectBy={selectBy}
									showBy={showBy}
									sortBy={sortBy}
									loading={loading}
									onChange={handleOnChange}
								/>
							</Stack>
						</Grow>
					) : (
						Boolean(label) && (
							<Typography variant="subtitle2" component="div" mt={1} className={"regular-btn-text"} sx={{ marginTop: "9px" }}>
								{label}
							</Typography>
						)
					)}
				</Paper>
			</Tooltip>
		</ClickAwayListener>
	);
}
TileSelectButton.propTypes = {
	className: PropTypes.string,
	data: PropTypes.array,
	icon: PropTypes.element,
	iconHover: PropTypes.element,
	label: PropTypes.string,
	tooltip: PropTypes.string,
	loading: PropTypes.bool,
	maxItems: PropTypes.number,
	noExpandible: PropTypes.bool,
	disabled: PropTypes.bool,
	selectBy: PropTypes.string,
	selectDefaultValue: PropTypes.object,
	showBy: PropTypes.string,
	sortBy: PropTypes.string,
	sx: PropTypes.object,
	onSelect: PropTypes.func,
};
